import { Button, Result } from "antd";
import { Component, type ErrorInfo, type ReactNode } from "react";
import { useTranslation } from "react-i18next";

function Fallback({ error, onRetry }: { error: Error; onRetry: () => void }) {
  const { t } = useTranslation("common");
  return (
    <Result
      status="error"
      title={t("error.title")}
      subTitle={error.message}
      extra={
        <Button type="primary" onClick={onRetry}>
          {t("error.retry")}
        </Button>
      }
    />
  );
}

interface State {
  error: Error | null;
}

/** Catches render errors per screen so one failing module does not blank the shell. */
export class ErrorBoundary extends Component<{ children: ReactNode; onError?: (e: Error, info: ErrorInfo) => void }, State> {
  override state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  override componentDidCatch(error: Error, info: ErrorInfo): void {
    this.props.onError?.(error, info);
  }

  override render() {
    if (this.state.error) return <Fallback error={this.state.error} onRetry={() => this.setState({ error: null })} />;
    return this.props.children;
  }
}
